// Instant Booking Service for BookAPT
// Handles one-click booking and instant payment processing

import SimpleStorage from '../utils/simpleStorage';
import { Platform } from 'react-native';
import type { VehicleProfile } from '../types/vehicle';
import { normalizeStoredServicePrice } from '../utils/servicePriceDisplay';

export interface InstantBooking {
  id: string;
  customerId: string;
  businessId: string;
  serviceId: string;
  serviceName: string;
  amount: number;
  currency: string;
  appointmentDate: string;
  appointmentTime: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  paymentMethod: string;
  workerId?: string;
  workerName?: string;
  createdAt: string;
  confirmedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  customerData: {
    name: string;
    phone: string;
    email: string;
    address?: string;
    preferences: string[];
    vehicleId?: string | null;
    vehicle?: VehicleProfile | null;
  };
  specialInstructions?: string;
  businessData: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
  serviceData: {
    name: string;
    duration: number;
    price: number | null;
    description: string;
    requirements: string[];
  };
}

export interface QuickBookOption {
  id: string;
  serviceName: string;
  duration: number;
  /** null = price on request (no fixed amount at booking). */
  price: number | null;
  description: string;
  icon: string;
  category: string;
  isPopular: boolean;
  requirements?: string[];
  availableSlots: string[];
  isProduct?: boolean; // Optional flag to indicate if this is a product
  unit?: string; // For products: 'lb', 'kg', 'piece', etc.
  stockQuantity?: number; // For products: stock quantity
  isInStock?: boolean; // For products: whether product is in stock
  options?: Array<{ // For products: product options (size, packaging, etc.)
    id: string;
    name: string;
    values: Array<{
      id: string;
      name: string;
      priceModifier?: number;
    }>;
    isRequired: boolean;
  }>;
  images?: string[]; // Image URLs (Firebase Storage or external) for products or services
  imageThumbnails?: string[]; // Small derivative of `images`, for fast list/card rendering
}

export interface BookingPreferences {
  preferredTimes: string[];
  preferredDays: string[];
  maxTravelDistance: number;
  budgetRange: { min: number; max: number };
  serviceCategories: string[];
  paymentMethods: string[];
  notificationPreferences: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
}

class InstantBookingService {
  private readonly INSTANT_BOOKINGS_KEY = 'instant_bookings';
  private readonly QUICK_BOOK_OPTIONS_KEY = 'quick_book_options';
  private readonly BOOKING_PREFERENCES_KEY = 'booking_preferences';

  // Default quick book options
  private readonly DEFAULT_QUICK_BOOK_OPTIONS: QuickBookOption[] = [
    {
      id: 'haircut_basic',
      serviceName: 'Basic Haircut',
      duration: 30,
      price: 45,
      description: 'Professional haircut and styling',
      icon: '💇‍♀️',
      category: 'hair',
      isPopular: true,
      requirements: ['Clean hair', 'No styling products'],
      availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00']
    },
    {
      id: 'haircut_premium',
      serviceName: 'Premium Haircut',
      duration: 45,
      price: 75,
      description: 'Premium haircut with wash and style',
      icon: '💇‍♀️',
      category: 'hair',
      isPopular: true,
      requirements: ['Clean hair', 'No styling products', 'Arrive 5 minutes early'],
      availableSlots: ['09:00', '10:30', '12:00', '14:30', '16:00']
    },
    {
      id: 'hair_color',
      serviceName: 'Hair Coloring',
      duration: 120,
      price: 120,
      description: 'Full hair coloring service',
      icon: '🎨',
      category: 'hair',
      isPopular: false,
      requirements: ['Clean hair', 'No previous color', 'Patch test required'],
      availableSlots: ['09:00', '10:00', '11:00']
    },
    {
      id: 'facial_basic',
      serviceName: 'Basic Facial',
      duration: 60,
      price: 80,
      description: 'Standard facial treatment',
      icon: '✨',
      category: 'beauty',
      isPopular: true,
      requirements: ['Clean face', 'No makeup', 'Arrive 10 minutes early'],
      availableSlots: ['10:00', '11:00', '14:00', '15:00', '16:00']
    },
    {
      id: 'massage_60min',
      serviceName: '60-Minute Massage',
      duration: 60,
      price: 100,
      description: 'Relaxing 60-minute massage',
      icon: '🧘‍♀️',
      category: 'wellness',
      isPopular: true,
      requirements: ['Clean body', 'No lotions', 'Arrive 15 minutes early'],
      availableSlots: ['09:00', '10:30', '12:00', '14:00', '15:30']
    },
    {
      id: 'manicure_basic',
      serviceName: 'Basic Manicure',
      duration: 45,
      price: 35,
      description: 'Standard manicure service',
      icon: '💅',
      category: 'beauty',
      isPopular: true,
      requirements: ['Clean nails', 'No polish', 'Arrive 5 minutes early'],
      availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00']
    }
  ];

  constructor() {
    this.initializeQuickBookOptions();
  }

  // Initialize quick book options
  private async initializeQuickBookOptions() {
    try {
      const existingOptions = await this.getQuickBookOptions();
      if (existingOptions.length === 0) {
        await this.saveQuickBookOptions(this.DEFAULT_QUICK_BOOK_OPTIONS);
      }
    } catch (error) {
      console.error('Error initializing quick book options:', error);
    }
  }

  // Get quick book options
  async getQuickBookOptions(): Promise<QuickBookOption[]> {
    try {
      // First try to get merchant's services
      const merchantServices = await this.getMerchantServices();
      if (merchantServices.length > 0) {
        return merchantServices;
      }

      // Fallback to stored quick book options
      const optionsJson = await SimpleStorage.getItem(this.QUICK_BOOK_OPTIONS_KEY);
      return optionsJson ? JSON.parse(optionsJson) : [];
    } catch (error) {
      console.error('Error getting quick book options:', error);
      return [];
    }
  }

  // Get merchant's services from service management
  private async getMerchantServices(): Promise<QuickBookOption[]> {
    try {
      // Get the current business owner
      const { AuthManager } = await import('../utils/authManager');
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      
      if (!businessOwner) {
        return [];
      }

      // Load services from service management
      const servicesJson = await SimpleStorage.getItem(`services_${businessOwner.id}`);
      if (!servicesJson) {
        return [];
      }

      const services = JSON.parse(servicesJson);
      
      // Convert services to QuickBookOption format
      return services
        .filter((service: any) => service.isActive)
        .map((service: any) => ({
          id: service.id,
          serviceName: service.name,
          duration: service.duration,
          price: normalizeStoredServicePrice(service.price),
          description: service.description,
          icon: service.icon,
          category: service.category,
          isPopular: true, // Mark all merchant services as popular
          requirements: service.requirements || [],
          availableSlots: this.generateTimeSlots(service.duration),
          images: Array.isArray(service.images) && service.images.length > 0 ? service.images : undefined,
          imageThumbnails: Array.isArray(service.imageThumbnails) && service.imageThumbnails.length > 0 ? service.imageThumbnails : undefined,
        }));
    } catch (error) {
      console.error('Error getting merchant services:', error);
      return [];
    }
  }

  // Generate available time slots for a service
  private generateTimeSlots(duration: number): string[] {
    const slots = [];
    const startHour = 9; // 9 AM
    const endHour = 17; // 5 PM
    const slotInterval = 30; // 30 minutes

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotInterval) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }

    return slots;
  }

  // Get popular services
  async getPopularServices(): Promise<QuickBookOption[]> {
    try {
      const options = await this.getQuickBookOptions();
      return options.filter(option => option.isPopular);
    } catch (error) {
      console.error('Error getting popular services:', error);
      return [];
    }
  }

  // Get services by category
  async getServicesByCategory(category: string): Promise<QuickBookOption[]> {
    try {
      const options = await this.getQuickBookOptions();
      return options.filter(option => option.category === category);
    } catch (error) {
      console.error('Error getting services by category:', error);
      return [];
    }
  }

  // Create instant booking
  async createInstantBooking(bookingData: {
    customerId: string;
    businessId: string;
    serviceId: string;
    appointmentDate: string;
    appointmentTime: string;
    paymentMethod: string;
    /** True when a real gateway (Stripe/Braintree/etc.) already charged the customer before this call —
     *  skips re-simulating payment so a genuinely successful charge can't be randomly marked failed. */
    paymentAlreadyProcessed?: boolean;
    workerId?: string;
    workerName?: string;
    customerData: {
      name: string;
      phone: string;
      email: string;
      address?: string;
      preferences: string[];
      vehicleId?: string | null;
      vehicle?: VehicleProfile | null;
    };
    service?: QuickBookOption; // Optional: pass service data directly to avoid lookup
  }): Promise<InstantBooking | null> {
    try {
      console.log('🔍 createInstantBooking called with:', bookingData);

      // Use provided service data if available, otherwise look it up
      let service: QuickBookOption | null | undefined = bookingData.service;
      if (!service) {
        service = await this.getServiceById(bookingData.serviceId);
        console.log('🔍 Found service via lookup:', service);
      if (!service) {
        console.error('❌ Service not found for ID:', bookingData.serviceId);
        throw new Error('Service not found');
        }
      } else {
        console.log('🔍 Using provided service data:', service);
      }
      const ensuredService = service as QuickBookOption;
      const resolvedPrice = normalizeStoredServicePrice(ensuredService.price);

      let effectivePaymentMethod = bookingData.paymentMethod;
      if (resolvedPrice === null && effectivePaymentMethod !== 'pay_later') {
        effectivePaymentMethod = 'pay_later';
      }

      // Get business data
      const business = this.getBusinessById(bookingData.businessId);
      console.log('🔍 Found business:', business);

      // Enforce business policy: if payment is required at booking, disallow pay_later
      // Best-effort lookup from Firestore (web) or inline business data if available.
      let paymentRequiredAtBooking = false;
      try {
        if ((business as any)?.paymentRequiredAtBooking) {
          paymentRequiredAtBooking = true;
        } else if (Platform.OS === 'web') {
          const { doc, getDoc } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
          if (initializeFirebase()) {
            const db = getWebDb();
            if (db) {
              const snap = await getDoc(doc(db, 'businesses', bookingData.businessId));
              const data = snap?.exists?.() ? snap.data() : null;
              paymentRequiredAtBooking = !!data?.paymentRequiredAtBooking;
            }
          }
        }
      } catch (e) {
        // fail open: if we can't load policy, keep existing behavior
      }
      if (paymentRequiredAtBooking && bookingData.paymentMethod === 'pay_later' && resolvedPrice !== null) {
        console.warn('⚠️ Payment required at booking; overriding pay_later to immediate payment');
        bookingData.paymentMethod = 'credit_card';
        effectivePaymentMethod = 'credit_card';
      }

      const instantBooking: InstantBooking = {
        id: `ib_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        customerId: bookingData.customerId,
        businessId: bookingData.businessId,
        serviceId: bookingData.serviceId,
        serviceName: ensuredService.serviceName,
        amount: resolvedPrice ?? 0,
        currency: 'USD',
        appointmentDate: bookingData.appointmentDate,
        appointmentTime: bookingData.appointmentTime,
        status: 'pending',
        paymentStatus: 'pending',
        paymentMethod: effectivePaymentMethod,
        workerId: bookingData.workerId,
        workerName: bookingData.workerName,
        createdAt: new Date().toISOString(),
        customerData: bookingData.customerData,
        businessData: {
          name: business.name,
          address: business.address,
          phone: business.phone,
          email: business.email
        },
        serviceData: {
          name: ensuredService.serviceName,
          duration: ensuredService.duration,
          price: resolvedPrice,
          description: ensuredService.description,
          requirements: ensuredService.requirements || []
        }
      };

      // Process payment based on payment method
      if (bookingData.paymentAlreadyProcessed) {
        // A real gateway (Stripe/Braintree/verified PayPal Orders API capture/etc.) already
        // charged the customer before this call. Trust that result instead of re-simulating
        // payment. Checked first so a verified PayPal capture isn't downgraded to "pending"
        // by the off-platform 'paypal' branch below.
        instantBooking.paymentStatus = 'paid';
        instantBooking.status = 'confirmed';
        instantBooking.confirmedAt = new Date().toISOString();
      } else if (effectivePaymentMethod === 'pay_later' || effectivePaymentMethod === 'paypal') {
        // Pay later, and off-platform PayPal (BookAPT never sees or processes these funds —
        // there is no way to verify completion from here), so payment is honestly "pending"
        // until the business confirms it themselves. Never simulate/guess a paid status.
        instantBooking.paymentStatus = 'pending';
        instantBooking.status = 'confirmed';
        instantBooking.confirmedAt = new Date().toISOString();
      } else {
        // Process payment immediately for other methods
        const paymentSuccess = await this.processInstantPayment(instantBooking);
        if (paymentSuccess) {
          instantBooking.paymentStatus = 'paid';
          instantBooking.status = 'confirmed';
          instantBooking.confirmedAt = new Date().toISOString();
        } else {
          instantBooking.paymentStatus = 'failed';
          instantBooking.status = 'cancelled';
          instantBooking.cancelledAt = new Date().toISOString();
          instantBooking.cancellationReason = 'Payment failed';
        }
      }

      // Save booking
      await this.saveInstantBooking(instantBooking);

      return instantBooking;
    } catch (error) {
      console.error('Error creating instant booking:', error);
      return null;
    }
  }

  // Process instant payment
  private async processInstantPayment(booking: InstantBooking): Promise<boolean> {
    try {
      // In a real app, this would process the actual payment
      // For demo purposes, we'll simulate payment processing
      console.log(`Processing instant payment for ${booking.serviceName} - $${booking.amount}`);
      
      // Simulate payment processing delay
      await new Promise<void>(resolve => setTimeout(resolve, 2000));
      
      // Simulate 95% success rate
      return Math.random() > 0.05;
    } catch (error) {
      console.error('Error processing instant payment:', error);
      return false;
    }
  }

  // Get service by ID
  private async getServiceById(serviceId: string): Promise<QuickBookOption | null> {
    try {
      // First try to get from quick book options
      const options = await this.getQuickBookOptions();
      let service = options.find(option => option.id === serviceId);
      
      if (service) {
        return service;
      }
      
      // If not found, try to get from the new step-by-step booking services
      const stepService = this.getStepByStepServiceById(serviceId);
      if (stepService) return stepService;
      
      return null;
    } catch (error) {
      console.error('Error getting service by ID:', error);
      return null;
    }
  }

  // Get service from step-by-step booking flow
  private getStepByStepServiceById(serviceId: string): QuickBookOption | null {
    const stepByStepServices: Record<string, QuickBookOption> = {
      // Auto Service
      'oil_change': { id: 'oil_change', serviceName: 'Oil Change', duration: 30, price: 35, description: 'Standard oil change', icon: '🛢️', category: 'auto', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
      'tire_rotation': { id: 'tire_rotation', serviceName: 'Tire Rotation', duration: 45, price: 25, description: 'Rotate tires for even wear', icon: '🛞', category: 'auto', isPopular: false, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
      'brake_check': { id: 'brake_check', serviceName: 'Brake Inspection', duration: 60, price: 50, description: 'Complete brake system check', icon: '🛑', category: 'auto', isPopular: true, availableSlots: ['09:00', '11:00', '14:00', '16:00'] },
      'battery_test': { id: 'battery_test', serviceName: 'Battery Test', duration: 15, price: 15, description: 'Test battery health', icon: '🔋', category: 'auto', isPopular: false, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
      
      // Hair Salon
      'haircut_basic': { id: 'haircut_basic', serviceName: 'Basic Haircut', duration: 30, price: 45, description: 'Professional haircut and styling', icon: '💇‍♀️', category: 'hair', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
      'haircut_wash': { id: 'haircut_wash', serviceName: 'Haircut + Wash', duration: 45, price: 60, description: 'Haircut with wash and style', icon: '💇‍♀️', category: 'hair', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
      'hair_color': { id: 'hair_color', serviceName: 'Hair Coloring', duration: 120, price: 120, description: 'Professional hair coloring', icon: '🎨', category: 'hair', isPopular: false, availableSlots: ['09:00', '14:00'] },
      'hair_styling': { id: 'hair_styling', serviceName: 'Hair Styling', duration: 60, price: 80, description: 'Special occasion styling', icon: '✨', category: 'hair', isPopular: false, availableSlots: ['10:00', '11:00', '15:00', '16:00'] },
      
      // Spa & Wellness
      'massage_swedish': { id: 'massage_swedish', serviceName: 'Swedish Massage', duration: 60, price: 100, description: 'Relaxing full-body massage', icon: '🧘‍♀️', category: 'spa', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
      'facial_basic': { id: 'facial_basic', serviceName: 'Basic Facial', duration: 60, price: 80, description: 'Standard facial treatment', icon: '✨', category: 'spa', isPopular: true, availableSlots: ['10:00', '11:00', '14:00', '15:00', '16:00'] },
      'deep_tissue': { id: 'deep_tissue', serviceName: 'Deep Tissue Massage', duration: 90, price: 150, description: 'Therapeutic deep tissue massage', icon: '💪', category: 'spa', isPopular: false, availableSlots: ['09:00', '14:00'] },
      'hot_stone': { id: 'hot_stone', serviceName: 'Hot Stone Massage', duration: 90, price: 180, description: 'Relaxing hot stone therapy', icon: '🔥', category: 'spa', isPopular: false, availableSlots: ['10:00', '15:00'] },
      
      // Nail Salon
      'manicure_basic': { id: 'manicure_basic', serviceName: 'Basic Manicure', duration: 30, price: 25, description: 'Standard manicure', icon: '💅', category: 'nails', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
      'pedicure_basic': { id: 'pedicure_basic', serviceName: 'Basic Pedicure', duration: 45, price: 35, description: 'Standard pedicure', icon: '🦶', category: 'nails', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
      'gel_manicure': { id: 'gel_manicure', serviceName: 'Gel Manicure', duration: 60, price: 45, description: 'Long-lasting gel manicure', icon: '💎', category: 'nails', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00', '16:00'] },
      'nail_art': { id: 'nail_art', serviceName: 'Nail Art', duration: 90, price: 65, description: 'Custom nail art design', icon: '🎨', category: 'nails', isPopular: false, availableSlots: ['10:00', '14:00', '15:00'] },
      
      // Medical & Dental
      'general_service': { id: 'general_service', serviceName: 'General Service', duration: 60, price: 50, description: 'General service', icon: '⚙️', category: 'Medical & Dental', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'], requirements: ['Valid ID'] },
    };
    
    return stepByStepServices[serviceId] || null;
  }

  // Get business data by ID
  private getBusinessById(businessId: string): any {
    const businesses: Record<string, any> = {
      'biz1': {
        id: 'biz1',
        name: 'Elite Hair Studio',
        address: '123 Main St, Arlington, TX 76010',
        phone: '(555) 123-4567',
        email: 'info@elitehair.com',
        category: 'Hair Salon',
        rating: 4.8,
        distance: '0.5 mi'
      },
      'biz2': {
        id: 'biz2',
        name: 'Quick Lube Auto',
        address: '456 Oak Ave, Arlington, TX 76012',
        phone: '(555) 234-5678',
        email: 'info@quicklube.com',
        category: 'Auto Service',
        rating: 4.6,
        distance: '1.2 mi'
      },
      'biz3': {
        id: 'biz3',
        name: 'Serenity Spa',
        address: '789 Pine St, Arlington, TX 76013',
        phone: '(555) 345-6789',
        email: 'info@serenityspa.com',
        category: 'Spa & Wellness',
        rating: 4.9,
        distance: '0.8 mi'
      },
      'biz4': {
        id: 'biz4',
        name: 'Nail Art Studio',
        address: '321 Elm St, Arlington, TX 76014',
        phone: '(555) 456-7890',
        email: 'info@nailart.com',
        category: 'Nail Salon',
        rating: 4.7,
        distance: '1.5 mi'
      },
      'test_business_1': {
        id: 'test_business_1',
        name: 'Test Business',
        address: '123 Test St, Arlington, TX 76010',
        phone: '(555) 000-0000',
        email: 'test@business.com',
        category: 'Test Category',
        rating: 4.0,
        distance: '0.0 mi'
      },
      'business_2': {
        id: 'business_2',
        name: 'Family Health Clinic',
        address: '555 Health Ave, Arlington, TX 76015',
        phone: '(555) 555-5555',
        email: 'info@familyhealth.com',
        category: 'Medical & Dental',
        rating: 4.5,
        distance: '1.0 mi'
      }
    };
    
    return businesses[businessId] || businesses['test_business_1'];
  }

  // Get instant bookings for user
  async getInstantBookings(userId: string): Promise<InstantBooking[]> {
    try {
      const bookingsJson = await SimpleStorage.getItem(this.INSTANT_BOOKINGS_KEY);
      const bookings: InstantBooking[] = bookingsJson ? JSON.parse(bookingsJson) : [];
      return bookings.filter(booking => booking.customerId === userId);
    } catch (error) {
      console.error('Error getting instant bookings:', error);
      return [];
    }
  }

  // Get instant bookings for business
  async getBusinessInstantBookings(businessId: string): Promise<InstantBooking[]> {
    try {
      const bookingsJson = await SimpleStorage.getItem(this.INSTANT_BOOKINGS_KEY);
      const bookings: InstantBooking[] = bookingsJson ? JSON.parse(bookingsJson) : [];
      return bookings.filter(booking => booking.businessId === businessId);
    } catch (error) {
      console.error('Error getting business instant bookings:', error);
      return [];
    }
  }

  // Cancel instant booking
  async cancelInstantBooking(bookingId: string, reason: string): Promise<boolean> {
    try {
      const bookings = await this.getAllInstantBookings();
      const booking = bookings.find(b => b.id === bookingId);
      
      if (!booking) {
        return false;
      }

      booking.status = 'cancelled';
      booking.cancelledAt = new Date().toISOString();
      booking.cancellationReason = reason;

      await this.updateInstantBooking(booking);
      return true;
    } catch (error) {
      console.error('Error cancelling instant booking:', error);
      return false;
    }
  }

  // Complete instant booking
  async completeInstantBooking(bookingId: string): Promise<boolean> {
    try {
      const bookings = await this.getAllInstantBookings();
      const booking = bookings.find(b => b.id === bookingId);
      
      if (!booking) {
        return false;
      }

      booking.status = 'completed';
      booking.completedAt = new Date().toISOString();

      await this.updateInstantBooking(booking);
      return true;
    } catch (error) {
      console.error('Error completing instant booking:', error);
      return false;
    }
  }

  // Get booking analytics
  async getBookingAnalytics(): Promise<{
    totalBookings: number;
    confirmedBookings: number;
    completedBookings: number;
    cancelledBookings: number;
    totalRevenue: number;
    averageBookingValue: number;
    conversionRate: number;
    popularServices: { [key: string]: number };
  }> {
    try {
      const bookings = await this.getAllInstantBookings();
      
      const analytics = {
        totalBookings: bookings.length,
        confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
        completedBookings: bookings.filter(b => b.status === 'completed').length,
        cancelledBookings: bookings.filter(b => b.status === 'cancelled').length,
        totalRevenue: bookings
          .filter(b => b.paymentStatus === 'paid')
          .reduce((sum, b) => sum + b.amount, 0),
        averageBookingValue: 0,
        conversionRate: 0,
        popularServices: {} as { [key: string]: number }
      };

      if (analytics.totalBookings > 0) {
        analytics.averageBookingValue = analytics.totalRevenue / analytics.confirmedBookings;
        analytics.conversionRate = (analytics.confirmedBookings / analytics.totalBookings) * 100;
      }

      // Count popular services
      bookings.forEach(booking => {
        analytics.popularServices[booking.serviceName] = 
          (analytics.popularServices[booking.serviceName] || 0) + 1;
      });

      return analytics;
    } catch (error) {
      console.error('Error getting booking analytics:', error);
      return {
        totalBookings: 0,
        confirmedBookings: 0,
        completedBookings: 0,
        cancelledBookings: 0,
        totalRevenue: 0,
        averageBookingValue: 0,
        conversionRate: 0,
        popularServices: {}
      };
    }
  }

  // Save instant booking (Firestore + local storage)
  private async saveInstantBooking(booking: InstantBooking): Promise<void> {
    try {
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const bookings = await this.getAllInstantBookings();
      bookings.push(booking);
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.INSTANT_BOOKINGS_KEY, JSON.stringify(bookings));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          // Use require() to ensure same module instance as unifiedAuthService
          const { doc, setDoc } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
          
          // Ensure Firebase is initialized and get named database
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  InstantBookingService: Firebase not initialized, skipping Firestore save');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  InstantBookingService: No Firebase database instance found, skipping Firestore save');
            } else {
              await setDoc(doc(firestoreDb, 'instant_bookings', booking.id), {
                ...booking,
                createdAt: booking.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ InstantBookingService: Instant booking saved to Firestore: ${booking.id}`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ InstantBookingService: Error saving instant booking to Firestore:', firestoreError);
          console.warn('⚠️  Instant booking saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
    } catch (error) {
      console.error('Error saving instant booking:', error);
    }
  }

  // Update instant booking (Firestore + local storage)
  private async updateInstantBooking(booking: InstantBooking): Promise<void> {
    try {
      const bookings = await this.getAllInstantBookings();
      const index = bookings.findIndex(b => b.id === booking.id);
      if (index !== -1) {
        bookings[index] = booking;
        
        // Save to local storage (for offline access)
        await SimpleStorage.setItem(this.INSTANT_BOOKINGS_KEY, JSON.stringify(bookings));
        
        // Also update in Firestore (for cross-browser/device access)
        if (Platform.OS === 'web') {
          try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const { getWebDb } = await import('./unifiedAuthService');
            const db = getWebDb();
            
            if (db) {
              await updateDoc(doc(db, 'instant_bookings', booking.id), {
                ...booking,
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ InstantBookingService: Instant booking updated in Firestore: ${booking.id}`);
            }
          } catch (firestoreError: any) {
            console.error('❌ InstantBookingService: Error updating instant booking in Firestore:', firestoreError);
            // Don't throw - local update succeeded
          }
        }
      }
    } catch (error) {
      console.error('Error updating instant booking:', error);
    }
  }

  // Get all instant bookings (Firestore first, then local fallback)
  private async getAllInstantBookings(): Promise<InstantBooking[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedBookings: InstantBooking[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          // Use require() to ensure same module instance as unifiedAuthService
          const { collection, getDocs } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
          
          // Ensure Firebase is initialized and get named database
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  InstantBookingService: Firebase not initialized, skipping Firestore load');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  InstantBookingService: No Firebase database instance found, skipping Firestore load');
            } else {
              const bookingsSnapshot = await getDocs(collection(firestoreDb, 'instant_bookings'));
              loadedBookings = bookingsSnapshot.docs.map((d: any) => ({
                id: d.id,
                ...d.data()
              } as InstantBooking));
              loadedFromFirestore = true;
              console.log(`✅ InstantBookingService: Loaded ${loadedBookings.length} instant bookings from Firestore`);
              
              // Sync to local storage
              await SimpleStorage.forceInitialize();
              await SimpleStorage.setItem(this.INSTANT_BOOKINGS_KEY, JSON.stringify(loadedBookings));
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  InstantBookingService: Could not load from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      const bookingsJson = await SimpleStorage.getItem(this.INSTANT_BOOKINGS_KEY);
        if (bookingsJson) {
          loadedBookings = JSON.parse(bookingsJson);
          console.log(`📋 InstantBookingService: Loaded ${loadedBookings.length} instant bookings from local storage`);
        }
      }
      
      return loadedBookings;
    } catch (error) {
      console.error('Error getting all instant bookings:', error);
      return [];
    }
  }

  // Save quick book options
  private async saveQuickBookOptions(options: QuickBookOption[]): Promise<void> {
    try {
      // Ensure SimpleStorage is initialized
      await SimpleStorage.forceInitialize();
      
      await SimpleStorage.setItem(this.QUICK_BOOK_OPTIONS_KEY, JSON.stringify(options));
    } catch (error) {
      console.error('Error saving quick book options:', error);
    }
  }
}

export const instantBookingService = new InstantBookingService();
export default InstantBookingService;
